import {
  columnGroupingModelType,
  DayGroupInterface,
  daysOfWeek,
  generateTimeSlots,
  InputRow,
  TimeSlotColumnInterface,
} from 'Features/SchedulerView/SchedulerView.types'
import { memo, useEffect, useMemo, useState, useRef, useCallback } from 'react'
import { GridCellParams, GridColDef, MuiEvent } from '@mui/x-data-grid-premium'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import { generateKitchenRows, KITCHEN } from './GenerateKitchenRows'
import { OutputPopper, TimeHeader } from './CommonComponents'
import { WorkstationDownTimeRowType } from 'Features/Scheduler/Scheduler.types'
import { RenderCell } from './RenderCell.view'
import { getSegmentBounds } from './CommonMethods'
import { Box } from '@mui/material'
import { useVerticalRuler } from './VerticalRuler.utils'

export const slotColumnCommonFields: Partial<ExtendedGridColDef> = {
  sortable: false,
  filterable: false,
  pinnable: false,
  hideable: false,
  resizable: false,
}

type KitchenScheduleProps = {
  data: InputRow[]
  left: number
  loading: boolean
  kitchenDownTime: WorkstationDownTimeRowType[]
}

export const generateColumnGroupingModel = () => {
  const timeSlots = generateTimeSlots()

  const columnGroupingModel: columnGroupingModelType[] = []

  daysOfWeek.forEach((day, dayIndex) => {
    const dayGroup: DayGroupInterface = {
      groupId: day,
      headerName: day,
      children: timeSlots.map(
        (time, hourIndex): TimeSlotColumnInterface => ({
          field: `hour-${dayIndex * 24 + hourIndex}`,
          valueGetter: (_, row) => {
            return row[`hour-${dayIndex * 24 + hourIndex}`]?.value ?? 0
          },
          headerName: time,
          width: 50,
          headerClassName: 'time-column',
          renderHeader: () => <TimeHeader time={time} />,
        })
      ),
    }
    columnGroupingModel.push(dayGroup)
  })

  return columnGroupingModel
}

export const KitchenSchedule = memo(
  ({ data, left, loading, kitchenDownTime }: KitchenScheduleProps) => {
    const verticalLineRef = useRef<HTMLDivElement | null>(null)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const gridApiRef = useRef<any>(null) // Store apiRef from BaseTable

    useEffect(() => {
      if (gridApiRef.current) {
        gridApiRef.current.scroll({ left })
      }
    }, [left])

    const tableRows = useMemo(() => {
      return generateKitchenRows(data, kitchenDownTime)
    }, [data, kitchenDownTime])

    const columnGroupingModel = useMemo(() => generateColumnGroupingModel(), [])

    const renderCellFunction = (params: GridCellParams) => {
      return <RenderCell rowData={params} showNumber={true} />
    }

    const columns = useMemo(() => {
      const columns: ExtendedGridColDef[] = [
        {
          field: 'kitchen',
          headerName: 'Kitchen',
          width: 100,
          align: 'center' as const,
          headerAlign: 'center' as const,
        },
        {
          field: 'runRateBase',
          headerName: 'Base Run Rate',
          width: 100,
          align: 'center' as const,
          headerAlign: 'center' as const,
          type: 'string' as const,
        },
        {
          field: 'location',
          headerName: 'Location',
          width: 100,
          align: 'center' as const,
          headerAlign: 'center' as const,
          type: 'string' as const,
        },
      ]
      columnGroupingModel.forEach((group) => {
        group.children.forEach((column: any) => {
          columns.push({
            type: 'string',
            renderCell: renderCellFunction,
            ...column,
            ...slotColumnCommonFields,
          })
        })
      })

      return columns
    }, [])

    // Popper — single state to avoid multiple re-renders on click
    const [popover, setPopover] = useState<{
      anchorEl: HTMLButtonElement | null
      cell: GridCellParams | null
      position: { top: number; left: number }
    }>({ anchorEl: null, cell: null, position: { top: 0, left: 0 } })

    const handleClose = useCallback(() => {
      setPopover((prev) => ({ ...prev, anchorEl: null }))
    }, [])

    const open = Boolean(popover.anchorEl)
    const id = open ? 'simple-popover' : undefined

    const selectedSegmentBounds = useMemo(
      () =>
        popover.cell
          ? getSegmentBounds(popover.cell.row, popover.cell.field)
          : null,
      [popover.cell]
    )

    const handleCellClick = useCallback(
      (params: GridCellParams, event: MuiEvent<React.MouseEvent>) => {
        if (params.value === 0) return
        const rect = (event.target as HTMLElement).getBoundingClientRect()
        setPopover({
          anchorEl: event.target as HTMLButtonElement,
          cell: params,
          position: { top: rect.top + rect.height, left: rect.left },
        })
      },
      []
    )
    // End Popper

    // Using the shared vertical ruler hook for consistent functionality
    const COMPONENT_ID = 'kitchen-view'
    const { handleMouseMove } = useVerticalRuler(
      containerRef,
      verticalLineRef,
      COMPONENT_ID
    )

    // All the vertical ruler functionality is now handled by the useVerticalRuler hook

    return (
      <Box
        ref={containerRef}
        sx={{ position: 'relative' }}
        onMouseMove={handleMouseMove}
      >
        <BaseTable
          data={tableRows}
          columns={columns}
          isLoading={loading}
          updateTableData={() => {}} // Read-only table
          isUploading={false}
          isSaving={false}
          showTableToolBar={false}
          checkBoxSelection={false}
          rowHeight={50}
          onCellClick={handleCellClick}
          onMouseMove={handleMouseMove}
          columnGroupingModel={columnGroupingModel}
          initialState={{
            pinnedColumns: {
              left: ['kitchen', 'runRateBase', 'location'],
              right: [],
            },
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          pinnedColumns={['kitchen', 'runRateBase', 'location']}
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: (theme) => theme.palette.grey[100],
              fontWeight: 600,
              fontSize: '0.8rem',
            },
            '& .MuiDataGrid-row': {
              marginBottom: '0px',
            },
            '& .MuiDataGrid-cell': {
              paddingLeft: 0,
              paddingRight: 0,
              paddingTop: '2px',
              paddingBottom: '2px',
              backgroundColor: 'rgba(220, 220, 220, 0.4)',
              borderRight: 'none',
            },
            '& .MuiDataGrid-cell--pinnedLeft': {
              backgroundColor: (theme) => theme.palette.background.paper,
              borderRight: (theme) => `1px solid ${theme.palette.divider}`,
            },
            '& .MuiDataGrid-row:hover': {
              backgroundColor: 'transparent',
            },
            '& .MuiDataGrid-cell:focus': {
              outline: 'none',
            },
            '& .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
          }}
          apiRefCallback={(apiRef) => {
            gridApiRef.current = apiRef.current
          }}
          downloadFileName="KitchenSchedule"
          tableHeight={500}
        />

        <OutputPopper
          rowName={popover.cell?.row.kitchen}
          tableType="Kitchen"
          start_time={selectedSegmentBounds?.segmentStart}
          end_time={selectedSegmentBounds?.segmentEnd}
          base_material_description={
            popover.cell?.row[popover.cell.field]?.base_material_description
          }
          slot_types={popover.cell?.row[popover.cell.field]?.slot_types}
          // Same reason as the line grid: a changeover slot said only when,
          // never what for.
          changeoverDetails={
            popover.cell?.row[popover.cell.field]?.changeover_details ?? []
          }
          crossReference={popover.cell?.row[popover.cell.field]?.lines}
          open={open}
          anchorEl={popover.anchorEl}
          anchorPosition={popover.position}
          handleClose={handleClose}
          id={id}
        />

        <Box
          ref={verticalLineRef}
          sx={{
            position: 'absolute',
            top: 0,
            height: '100%',
            left: 0,
            width: '2px',
            backgroundColor: 'rgba(255, 0, 17, 0.7)',
            zIndex: 1000,
            pointerEvents: 'none',
          }}
        />
      </Box>
    )
  }
)
